/** Minimal hast-like shape — avoids a direct `hast` / `@types/hast` dependency. */
interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
}

type Row = Record<string, string>;

function isHastNode(value: unknown): value is HastNode {
  return typeof value === 'object' && value !== null && 'type' in value && typeof (value as HastNode).type === 'string';
}

function isElement(node: HastNode): node is HastNode & { tagName: string; children: HastNode[] } {
  return node.type === 'element' && typeof node.tagName === 'string';
}

function isText(node: HastNode): node is HastNode & { value: string } {
  return node.type === 'text' && typeof node.value === 'string';
}

/** Flatten nested hast content to plain text (links, code, emphasis → their visible text). */
function hastText(node: HastNode): string {
  if (isText(node)) return node.value;
  if (isElement(node)) return (node.children ?? []).map(hastText).join('');
  return '';
}

function cellElements(tr: HastNode): HastNode[] {
  return (tr.children ?? []).filter((child) => isElement(child) && (child.tagName === 'th' || child.tagName === 'td'));
}

function collectRows(table: HastNode): string[][] {
  const rows: string[][] = [];

  for (const child of table.children ?? []) {
    if (!isElement(child)) continue;

    if (child.tagName === 'thead' || child.tagName === 'tbody') {
      for (const tr of child.children ?? []) {
        if (!isElement(tr) || tr.tagName !== 'tr') continue;
        rows.push(cellElements(tr).map((cell) => hastText(cell).trim()));
      }
      continue;
    }

    // Some pipelines emit <tr> directly under <table>.
    if (child.tagName === 'tr') {
      rows.push(cellElements(child).map((cell) => hastText(cell).trim()));
    }
  }

  return rows;
}

/** Make header keys unique so duplicate markdown headers don't collapse row values. */
export function uniquifyHeaders(headers: string[]): string[] {
  const used = new Set<string>();
  return headers.map((raw, index) => {
    const base = raw.trim() || `Column ${index + 1}`;
    let candidate = base;
    for (let n = 2; used.has(candidate); n++) candidate = `${base} (${n})`;
    used.add(candidate);
    return candidate;
  });
}

export interface MarkdownTableData {
  headers: string[];
  rows: Row[];
}

/**
 * Convert a react-markdown / hast `<table>` node into row records for the shared Table.
 * Returns null when the node isn't a usable table (no header row).
 */
export function extractMarkdownTable(node: unknown): MarkdownTableData | null {
  if (!isHastNode(node) || !isElement(node) || node.tagName !== 'table') return null;

  const matrix = collectRows(node);
  if (matrix.length === 0) return null;

  const headers = uniquifyHeaders(matrix[0] ?? []);
  if (headers.length === 0) return null;

  const rows = matrix.slice(1).map((cells) => {
    const row: Row = {};
    for (let i = 0; i < headers.length; i++) {
      row[headers[i]!] = cells[i] ?? '';
    }
    return row;
  });

  return { headers, rows };
}
